import {
  BlockQuote,
  CheckCircle,
  Close,
  Dot,
  Splash,
} from '@/client/components/icons/ContinuIcons';
import {
  Box,
  Button,
  ButtonGroup,
  Circle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
  useToken,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Notification } from '../../types/Notification';
import NotificationMessage from '../data-display/notifications/NotificationMessage';
import { formatNotificationDate } from '@/client/utils/formatNotificationDate';
import { useGetPublicImage } from '@/client/services/hooks/media/useGetPublicImage';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import v3ApiService from '../../services/api/clients/v3ApiClient';

// TODO: Set correct icon based on type returned from https://continu.atlassian.net/browse/DEV-13242

type UserNotificationsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  unreadNotifications: Notification[] | undefined;
  readNotifications: Notification[] | undefined;
};

export default function UserNotificationsDrawer({
  isOpen,
  onClose,
  unreadNotifications,
  readNotifications,
}: UserNotificationsDrawerProps) {
  const { t } = useTranslation();
  const { getPublicImage } = useGetPublicImage();
  const queryClient = useQueryClient();
  const [isRead, setIsRead] = useState<boolean>(false);
  const [brandButtonColor] = useToken('colors', ['brand.button']);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const archiveNotificationMutation = useMutation({
    mutationFn: (id: string) => v3ApiService.archiveNotification(id),
    onSuccess: () => queryClient.refetchQueries({ queryKey: ['notifications'] }),
  });

  const notifications = isRead ? readNotifications : unreadNotifications;

  const buttonStyles = {
    border: '1px solid',
    borderRadius: 'full',
    fontSize: '14px',
    fontWeight: 600,
    paddingX: 3,
    paddingY: 2,
  };

  const activeButtonStyles = {
    ...buttonStyles,
    backgroundColor: 'brand.button',
    borderColor: 'brand.button',
    color: 'white',
    hover: {
      backgroundColor: 'brand.button',
      color: 'white',
    },
  };

  const inactiveButtonStyles = {
    ...buttonStyles,
    backgroundColor: 'white',
    color: 'neutral.900',
    borderColor: 'neutral.200',
    _hover: {
      backgroundColor: 'neutral.100',
      color: 'neutral.1000',
    },
  };

  // const placement = isMobile ? 'bottom' : 'right';
  const placement = 'right';

  return (
    <Drawer
      size={{ base: 'full', md: 'md', lg: 'lg' }}
      placement={placement}
      onClose={onClose}
      isOpen={isOpen}
    >
      <DrawerOverlay backgroundColor="rgba(0, 0, 0, 0.3)" backdropFilter="blur(1px)" />

      <DrawerContent
        marginRight={{ base: 0, md: 4 }}
        marginTop={{ base: 0, md: 14 }}
        marginBottom={{ base: 0, md: 4 }}
        borderRadius="16px"
      >
        <DrawerHeader
          borderBottomWidth="1px"
          borderBottomColor="neutral.100"
          paddingLeft={6}
          paddingRight={4}
          paddingTop={5}
          paddingBottom={4}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize="18px" fontWeight={600} color="baseBlack">
              {t('notifications')}
            </Text>

            <Close
              boxSize={{ base: 4, md: 3 }}
              color="neutral.1000"
              onClick={onClose}
              _hover={{ cursor: 'pointer', color: 'neutral.1500' }}
            />
          </Flex>
        </DrawerHeader>

        <DrawerBody paddingX={6} paddingTop={0}>
          <Box paddingY={7} position="sticky" top={0} backgroundColor="white" zIndex={1}>
            <ButtonGroup>
              <Button
                sx={!isRead ? activeButtonStyles : inactiveButtonStyles}
                onClick={() => setIsRead(false)}
              >
                <HStack>
                  <Text>{t('notifications.unread')}</Text>

                  <Circle
                    paddingY="3px"
                    paddingX="5px"
                    fontSize="10px"
                    backgroundColor={!isRead ? 'whiteAlpha.400' : 'neutral.100'}
                  >
                    {unreadNotifications?.length}
                  </Circle>
                </HStack>
              </Button>

              <Button
                sx={isRead ? activeButtonStyles : inactiveButtonStyles}
                isDisabled={!readNotifications || readNotifications.length === 0}
                onClick={() => setIsRead(true)}
              >
                {t('notifications.read')}
              </Button>
            </ButtonGroup>
          </Box>

          {notifications && notifications.length === 0 && (
            <VStack>
              <Image
                src={getPublicImage('continu/app-images/no-messages--social-media-no-messages.svg')}
                alt="No Messages"
              />

              <Text fontSize="14px" fontWeight={500} color="neutral.600">
                {t('notifications.noUnread')}
              </Text>
            </VStack>
          )}

          {notifications && notifications.length > 0 && (
            <Stack>
              {notifications.map((notification) => (
                <Flex
                  key={notification._id}
                  alignItems="baseline"
                  paddingY={8}
                  borderBottom="1px"
                  borderBottomColor="gray.200"
                  maxWidth="100%"
                  wordBreak="break-word"
                >
                  <Icon as={Splash} boxSize={3} color={isRead ? 'neutral.300' : 'red.500'} />

                  <Stack flex={1} spacing={2} marginLeft={2} marginRight={4}>
                    <NotificationMessage message={notification.message} onClose={onClose} />

                    <Text fontSize="12px" fontWeight={500} color="neutral.1000">
                      {formatNotificationDate(notification.createdAt)}
                    </Text>
                  </Stack>

                  {!isRead && (
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label="Archive notification"
                      icon={<Close color="neutral.800" boxSize={2.5} />}
                      _hover={{
                        backgroundColor: 'neutral.50',
                      }}
                      onClick={() => archiveNotificationMutation.mutate(notification._id)}
                    />
                  )}
                </Flex>
              ))}
            </Stack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
